import { PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { clientConfig } from '@/config/clientConfig';
import { Notifications } from '@/features/notification/components/Notifications';
import { COLORS } from '@/shared/constants/colors';
import { useBreakpoints } from '@/shared/hooks/useBreakpoints';
import { useProfileStore } from '@/shared/store/profile.store';
import { useSensitiveValuesVisibilityStore } from '@/shared/store/sensitive-values-visibility.store';
import { BFC } from '@/shared/types';
import { Icon } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';
import { Tooltip } from '@/shared/uikitV2/antd/Tooltip';
import { Button } from '@/shared/uikitV2/Button';

import { Breadcrumb, BreadcrumbItem } from '../../breadcrumb';
import { media } from '../media';

type PageHeaderProps = PropsWithChildren & {
  title: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  height?: 'h56' | 'h64' | 'h88';
  hideMenuButtons?: boolean;
};

// TODO: rework for all pages related height
export const PageHeader: BFC<PageHeaderProps> = ({
  title,
  breadcrumbs,
  height,
  hideMenuButtons,
  children,
  style,
}) => {
  const { t } = useTranslation('shared/components', { keyPrefix: 'pageHeader' });
  const { toggleVisibility, isVisible } = useSensitiveValuesVisibilityStore();
  const { isBeneficiary } = useProfileStore();
  const { sm } = useBreakpoints();

  if (sm) {
    return <ActionButtons>{children}</ActionButtons>;
  }

  return (
    <Container style={style} className={height}>
      <Left>
        {breadcrumbs?.length && <Breadcrumb items={breadcrumbs} />}
        <Title size={28} bold>
          {title}
        </Title>
      </Left>
      <Right>
        {!hideMenuButtons && (
          <MenuButtons>
            <Tooltip
              placement="bottom"
              title={
                isVisible ? t('sensitiveIconTooltip.visible') : t('sensitiveIconTooltip.hidden')
              }
            >
              <StyledButton
                type="default"
                size="middle"
                onClick={toggleVisibility}
                icon={<Icon name={isVisible ? 'eye' : 'eye-slash'} size="s20" />}
              />
            </Tooltip>
            {!isBeneficiary && !clientConfig.isExported && (
              <Notifications>
                {({ toggleShow }) => (
                  <StyledButton
                    type="default"
                    size="middle"
                    onClick={toggleShow}
                    icon={<Icon size="s20" name="bell" />}
                  />
                )}
              </Notifications>
            )}
          </MenuButtons>
        )}
        <ActionButtons>{children}</ActionButtons>
      </Right>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-block: 28px;
  gap: 8px;

  &&&.h56 {
    height: 56px;
  }

  &&&.h64 {
    height: 64px;
  }

  &&&.h88 {
    height: 88px;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  ${media.sm} {
    justify-content: end;
    margin-bottom: 16px;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MenuButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${media.sm} {
    gap: 0;
  }
`;

const StyledButton = styled(Button)`
  &&& {
    background-color: ${COLORS.systemLight100};
    width: 40px;
    height: 40px;
    border-radius: 50%;
    color: ${COLORS.systemDark300};
    border: none;
  }
`;
